import React, { useEffect, useState } from 'react';
import { apiAdmin } from '../../services/apiService';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

interface IntegrationPattern {
  id: string;
  name: string;
  description: string;
  parameter_format: string;
  request_method: string;
  url_pattern: string;
  parameter_name: string;
  request_parameters?: Record<string, string>;
  headers?: Record<string, string>;
  country_code_format?: string;
  is_universal_search?: boolean;
  example: string;
  placeholders: string[];
}

interface ParameterFormatGuideProps {
  onApplyPattern?: (pattern: IntegrationPattern) => void;
  compact?: boolean;
}

export const ParameterFormatGuide: React.FC<ParameterFormatGuideProps> = ({
  onApplyPattern,
  compact = false,
}) => {
  const [patterns, setPatterns] = useState<IntegrationPattern[]>([]);
  const [placeholders, setPlaceholders] = useState<{ name: string; description: string }[]>([]);
  const [expanded, setExpanded] = useState(!compact);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiAdmin.getIntegrationPatterns()
      .then((data) => {
        setPatterns(data.patterns || []);
        setPlaceholders(data.placeholders || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 text-left"
      >
        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
          Integration patterns & placeholders
        </span>
        <span className="text-gray-400 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              Click a pattern to apply its configuration to the form:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {patterns.map((pattern) => (
                <div
                  key={pattern.id}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:border-indigo-400 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{pattern.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{pattern.description}</p>
                      <code className="text-xs text-indigo-600 dark:text-indigo-400 block mt-1 truncate">
                        {pattern.example}
                      </code>
                    </div>
                    {onApplyPattern && (
                      <Button
                        type="button"
                        variant="secondary"
                        className="text-xs px-2 py-1 shrink-0"
                        onClick={() => onApplyPattern(pattern)}
                      >
                        Apply
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <Badge variant="secondary">{pattern.request_method}</Badge>
                    <Badge variant="secondary">{pattern.parameter_format}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Available placeholders</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {placeholders.map((ph) => (
                <div key={ph.name} className="text-xs">
                  <code className="text-indigo-600 dark:text-indigo-400">{ph.name}</code>
                  <span className="text-gray-500 ml-1">— {ph.description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
